import { apiClient, setAuthToken } from './api'; // Import setAuthToken
import { LoginRequest, LoginResponse, UserDTO, ApiResponse } from '../types/backend'; // Add ApiResponse to imports

class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      
      // Specify the full response structure from the backend
      const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/signin', credentials);
      
      // THE FIX: Access response.data.data to get the actual login payload
      const loginData = response.data.data;
      
      
      if (loginData.token) {
        localStorage.setItem('authToken', loginData.token);
        localStorage.setItem('user', JSON.stringify({
          id: loginData.id,
          username: loginData.username,
          email: loginData.email,
          role: loginData.role
        }));
        setAuthToken(loginData.token); // Immediately set header for subsequent requests
      }
      
      return loginData;
    } catch (error: any) {
      console.error('❌ Login failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
      
      // Enhanced error message extraction
      let errorMessage = 'Login failed';
      
      if (error.response) {
        // Server responded with error
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          errorMessage = data?.message || 'Bad credentials';
        } else if (status === 403) {
          errorMessage = 'Account is disabled or locked';
        } else if (status === 404) {
          errorMessage = 'Authentication service not available';
        } else if (status === 500) {
          errorMessage = 'Server error occurred. Please try again later.';
        } else if (status === 503) {
          errorMessage = 'Service temporarily unavailable';
        } else {
          errorMessage = data?.message || data?.error || `Login failed (HTTP ${status})`;
        }
      } else if (error.request) {
        // Request made but no response received
        errorMessage = 'Network Error - Unable to reach server';
      } else {
        // Something else happened
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      throw new Error(errorMessage);
    }
  }

  async register(userData: {
    username: string;
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }): Promise<{ message: string }> {
    try {
      
      const response = await apiClient.post('/auth/signup', userData);
      
      return response.data;
    } catch (error: any) {
      console.error('❌ Registration failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  }

  async getCurrentUser(): Promise<UserDTO> {
    try {
      const response = await apiClient.get<ApiResponse<UserDTO>>('/auth/me');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get user info');
    }
  }

  async refreshToken(): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/refresh');
      const loginData = response.data.data;
      
      if (loginData.token) {
        localStorage.setItem('authToken', loginData.token);
        setAuthToken(loginData.token); // Immediately apply the refreshed token
      }
      
      return loginData;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    }
  }

  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setAuthToken(null); // Clear the token from headers
  }

  getStoredUser(): UserDTO | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export const authService = new AuthService();
export default authService;